import React, { useRef, useEffect, useCallback, useState, useImperativeHandle, forwardRef } from 'react';
import { useCanvas } from '../../hooks/useCanvas';
import { Play, Pause } from 'lucide-react';
import LiteTimeline from './LiteTimeline';

const LiteCanvasArea = forwardRef(({ onStageBoundsChange }, ref) => {
  const containerRef = useRef(null);
  const controlsBarRef = useRef(null);
  const [stageBounds, setStageBounds] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [controlsPinned, setControlsPinned] = useState(false);
  const [overlayIcon, setOverlayIcon] = useState(null); // 'play' | 'pause' | null
  const overlayKeyRef = useRef(0);
  const hideTimeoutRef = useRef(null);
  const mouseDownPosRef = useRef(null); // Track mousedown position to distinguish click vs drag

  const {
    viewport,
    pixiInitialized,
    centerAndFitViewport,
    canvasSize,
    togglePlayback,
    isPlaying,
  } = useCanvas(containerRef);

  // Calculate the screen-space bounds of the canvas stage
  const updateStageBounds = useCallback(() => {
    if (!viewport || !containerRef.current) return;

    const topLeft = viewport.toScreen(0, 0);
    const bottomRight = viewport.toScreen(canvasSize.width, canvasSize.height);

    const newBounds = {
      left: topLeft.x,
      top: topLeft.y,
      width: bottomRight.x - topLeft.x,
      height: bottomRight.y - topLeft.y,
    };
    setStageBounds(newBounds);
    if (onStageBoundsChange) onStageBoundsChange(newBounds);
  }, [viewport, canvasSize, onStageBoundsChange]);

  // Disable all PIXI interactivity so elements can't be selected/moved
  useEffect(() => {
    if (!pixiInitialized || !viewport) return;

    if (viewport.interactiveContainer) {
      viewport.interactiveContainer.eventMode = 'none';
      viewport.interactiveContainer.interactiveChildren = false;
    }
    if (viewport.visualContainer) {
      viewport.visualContainer.eventMode = 'none';
      viewport.visualContainer.interactiveChildren = false;
    }
    viewport.eventMode = 'none';
    viewport.interactiveChildren = false;
  }, [pixiInitialized, viewport]);

  // Handle resize
  useEffect(() => {
    if (!containerRef.current || !pixiInitialized || !viewport) return;

    const getPadding = () => window.innerWidth < 640 ? 16 : 40;

    const resizeObserver = new ResizeObserver(() => {
      viewport.resize(
        containerRef.current.offsetWidth,
        containerRef.current.offsetHeight
      );
      requestAnimationFrame(() => {
        centerAndFitViewport(getPadding());
        requestAnimationFrame(() => updateStageBounds());
      });
    });

    resizeObserver.observe(containerRef.current);

    requestAnimationFrame(() => {
      centerAndFitViewport(getPadding());
      requestAnimationFrame(() => updateStageBounds());
    });

    return () => resizeObserver.disconnect();
  }, [pixiInitialized, viewport, centerAndFitViewport, updateStageBounds]);

  // Show controls on mouse activity, auto-hide after delay (unless pinned)
  const revealControls = useCallback(() => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (!controlsPinned) {
      hideTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2500);
    }
  }, [controlsPinned]);

  // Expose revealControls to parent via ref
  useImperativeHandle(ref, () => ({ revealControls }), [revealControls]);

  // Keep controls visible while hovering the controls bar itself
  const handleControlsEnter = useCallback(() => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setShowControls(true);
  }, []);

  const handleControlsLeave = useCallback(() => {
    if (controlsPinned) return; // Don't auto-hide when pinned
    hideTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 1500);
  }, [controlsPinned]);

  // Callback for LiteTimeline to pin/unpin controls
  const handlePinControls = useCallback((pinned) => {
    setControlsPinned(pinned);
    if (pinned) {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      setShowControls(true);
    } else {
      // Start auto-hide timer when unpinned
      hideTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1500);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Track mousedown position so we can distinguish a genuine click from a drag/scrub
  const handleStageMouseDown = useCallback((e) => {
    // Don't register mousedown if it originated inside the timeline controls bar
    if (controlsBarRef.current && controlsBarRef.current.contains(e.target)) return;
    mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  // Only toggle play/pause if the mouse hasn't moved significantly (i.e. it's a real click, not a scrub)
  const handleStageClick = useCallback((e) => {
    // Don't toggle playback if the click originated inside the timeline controls bar
    if (controlsBarRef.current && controlsBarRef.current.contains(e.target)) return;

    const CLICK_THRESHOLD = 5; // pixels
    if (mouseDownPosRef.current) {
      const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
      const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
      if (dx > CLICK_THRESHOLD || dy > CLICK_THRESHOLD) {
        // User dragged — don't toggle playback
        mouseDownPosRef.current = null;
        return;
      }
    }
    mouseDownPosRef.current = null;

    // Show the icon for the state we're transitioning TO
    // isPlaying is the current state BEFORE toggle, so if playing → show pause, if paused → show play
    const nextIcon = isPlaying ? 'pause' : 'play';
    overlayKeyRef.current += 1;
    setOverlayIcon({ type: nextIcon, key: overlayKeyRef.current });

    togglePlayback();
    revealControls();

    // Clear the icon after the animation completes
    setTimeout(() => {
      setOverlayIcon(null);
    }, 600);
  }, [togglePlayback, revealControls, isPlaying]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* PIXI canvas container — clip-path rounds the visible stage area (CSS only, no export impact) */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          ...(stageBounds ? {
            clipPath: `inset(${stageBounds.top}px ${containerRef.current ? containerRef.current.offsetWidth - stageBounds.left - stageBounds.width : 0}px ${containerRef.current ? containerRef.current.offsetHeight - stageBounds.top - stageBounds.height : 0}px ${stageBounds.left}px round 12px)`,
          } : {}),
        }}
      />

      {/* Stage overlay — click/tap to play, hover to show controls */}
      {stageBounds && (
        <div
          onMouseDown={handleStageMouseDown}
          onClick={handleStageClick}
          onMouseMove={revealControls}
          onTouchStart={revealControls}
          style={{
            position: 'absolute',
            left: stageBounds.left,
            top: stageBounds.top,
            width: stageBounds.width,
            height: stageBounds.height,
            borderRadius: '12px',
            overflow: 'hidden',
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          {/* Big center play/pause flash icon */}
          {overlayIcon && (
            <div
              key={overlayIcon.key}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 20,
                animation: 'liteOverlayFlash 0.6s ease-out forwards',
              }}
            >
              <div
                className="w-12 h-12 sm:w-[72px] sm:h-[72px]"
                style={{
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 0, 0, 0.55)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {overlayIcon.type === 'play' ? (
                  <Play className="w-6 h-6 sm:w-9 sm:h-9" color="white" fill="white" style={{ marginLeft: '3px' }} />
                ) : (
                  <Pause className="w-6 h-6 sm:w-9 sm:h-9" color="white" fill="white" />
                )}
              </div>
            </div>
          )}

          {/* Bottom gradient + controls bar */}
          <div
            ref={controlsBarRef}
            onMouseEnter={handleControlsEnter}
            onMouseLeave={handleControlsLeave}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              transition: 'opacity 0.3s ease',
              opacity: showControls ? 1 : 0,
              pointerEvents: showControls ? 'auto' : 'none',
              cursor: 'default',
            }}
          >
            {/* Gradient fade */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: '120px',
                background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0) 100%)',
              }}
            />
            {/* Controls */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <LiteTimeline onPinControls={handlePinControls} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default LiteCanvasArea;
